"use client";

import { useEffect, useState, ReactNode } from "react";
import { bg } from "@/lib/tokens/semantic";
import { getAllowedIds, getGuestIds, isOpenAccess } from "@/lib/auth-config";
import { AccessDenied } from "./AccessDenied";
import { ServerPending } from "./ServerPending";

const ALLOWED_IDS = getAllowedIds();
// Public deployment: any Telegram user is allowed (bot is открытый).
const OPEN_ACCESS = isOpenAccess();
// Guests see only Vova's flow page (/agents/flow with session=vova).
const GUEST_IDS = getGuestIds();

/** Guest scope: /agents/flow with session=vova; "/" is a transit (Home redirects). */
function isVovaGuestScope(): boolean {
  const { pathname, search } = window.location;
  if (pathname === "/") return true;
  if (!pathname.startsWith("/agents/flow")) return false;
  const sp = new URLSearchParams(search);
  const start = sp.get("tgWebAppStartParam")
    ?? window.Telegram?.WebApp?.initDataUnsafe?.start_param ?? "";
  const session = sp.get("session") ?? (start ? start.split("-")[0] : "");
  return session === "" || session === "vova";
}

interface AuthGateProps {
  children: ReactNode;
}

export interface TenantInfo {
  status: string;
  /** Контракт №8: managed | local | none | unknown */
  mode: string;
}

/** Публичный юзер прошёл по подписи, но данных у него нет, пока нет
 *  active-тенанта (managed-сервер поднят и авторизован). Спрашиваем свой
 *  /api/tenant/status — сервер сам решает по initData, заглушку или контент. */
async function fetchTenantStatus(): Promise<TenantInfo> {
  try {
    const initData = window.Telegram?.WebApp?.initData ?? "";
    const resp = await fetch("/api/tenant/status", {
      headers: { "X-Init-Data": initData },
    });
    if (!resp.ok) return { status: "unknown", mode: "unknown" };
    const body = await resp.json();
    return { status: body.status ?? "unknown", mode: body.mode ?? "unknown" };
  } catch {
    return { status: "unknown", mode: "unknown" };
  }
}

export function AuthGate({ children }: AuthGateProps) {
  const [status, setStatus] = useState<"loading" | "allowed" | "denied">("loading");
  const [tenant, setTenant] = useState<TenantInfo | null>(null);
  // QA-крючок (только dev-сборка): ?gate=pending:<status>:<mode>
  // форсит гейт-экран без Telegram-контекста — для shot/burst-QA (ср. ?upoll).
  const [qaGate, setQaGate] = useState<string | null>(null);

  useEffect(() => {
    if (process.env.NODE_ENV !== "production") {
      const qa = new URLSearchParams(window.location.search).get("gate");
      if (qa) { setQaGate(qa); return; }
    }
    // Expand WebApp immediately to prevent black bar
    const tg = window.Telegram?.WebApp;
    if (tg) {
      tg.ready();
      tg.expand();
    }

    let attempts = 0;
    const maxAttempts = 10;
    // Cleanup-инвариант: guard от setState-after-unmount + снятие retry-таймера.
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const checkAuth = () => {
      if (!alive) return;
      const tg = window.Telegram?.WebApp;
      const userId = tg?.initDataUnsafe?.user?.id;

      if (userId) {
        const guest = GUEST_IDS.includes(userId) && isVovaGuestScope();
        if (ALLOWED_IDS.includes(userId) || guest) {
          setStatus("allowed");
        } else if (OPEN_ACCESS) {
          // Открытый бот: подпись есть, но контент — только с active-тенантом.
          fetchTenantStatus().then((t) => {
            if (!alive) return; // промис мог резолвнуться после unmount
            setTenant(t);
            setStatus(t.status === "active" ? "allowed" : "denied");
          });
        } else {
          setStatus("denied");
          console.warn(`[AuthGate] Access denied for user ${userId}`);
        }
        return;
      }

      attempts++;
      if (attempts < maxAttempts) {
        // Retry with exponential backoff
        timer = setTimeout(checkAuth, 100 * attempts);
      } else {
        // Give up after max attempts
        setStatus("denied");
        console.warn("[AuthGate] No Telegram context after retries");
      }
    };

    // Start checking
    checkAuth();

    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
    };
  }, []);

  if (qaGate?.startsWith("pending:")) {
    const [, qaStatus, qaMode] = qaGate.split(":");
    return <ServerPending status={qaStatus ?? "none"} mode={qaMode} />;
  }

  if (status === "loading") {
    // Just background — skeleton will show after auth
    return <div style={{ minHeight: "100dvh", background: bg.base }} />;
  }

  if (status === "denied") {
    if (OPEN_ACCESS && tenant) {
      // Не active (в т.ч. mode=none) — дружелюбная заглушка по статусу;
      // сам выбор сервера (мощнее/локально/без) живёт в БОТЕ, не здесь.
      return <ServerPending status={tenant.status} mode={tenant.mode} />;
    }
    return <AccessDenied />;
  }

  return <>{children}</>;
}
